<template>
  <div class="i-editor-pro-wrap">
    <!-- 工具栏容器，Quill 会自动填充 -->
    <div ref="toolbar" class="editor-toolbar">
      <span class="ql-formats">
        <select class="ql-header">
          <option value="1">标题1</option>
          <option value="2">标题2</option>
          <option value="3">标题3</option>
          <option value="">正文</option>
        </select>
      </span>
      <span class="ql-formats">
        <select class="ql-size">
          <option value="12px">12</option>
          <option value="14px" selected>14</option>
          <option value="16px">16</option>
          <option value="18px">18</option>
          <option value="20px">20</option>
          <option value="24px">24</option>
          <option value="28px">28</option>
          <option value="32px">32</option>
        </select>
      </span>
      <span class="ql-formats">
        <button class="ql-bold" title="粗体"></button>
        <button class="ql-italic" title="斜体"></button>
        <button class="ql-underline" title="下划线"></button>
        <button class="ql-strike" title="删除线"></button>
      </span>
      <span class="ql-formats">
        <select class="ql-color" title="字体颜色"></select>
        <select class="ql-background" title="背景颜色"></select>
      </span>
      <span class="ql-formats">
        <button class="ql-list" value="ordered" title="有序列表"></button>
        <button class="ql-list" value="bullet" title="无序列表"></button>
        <button class="ql-indent" value="-1" title="减少缩进"></button>
        <button class="ql-indent" value="+1" title="增加缩进"></button>
      </span>
      <span class="ql-formats">
        <select class="ql-align" title="对齐方式"></select>
      </span>
      <span class="ql-formats">
        <button class="ql-link" title="插入链接"></button>
        <button class="ql-image" title="插入图片"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-blockquote" title="引用"></button>
        <button class="ql-code-block" title="代码块"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-clean" title="清除格式"></button>
      </span>
    </div>

    <!-- 编辑区 -->
    <div ref="editorBox" class="editor-content" :style="{ 'min-height': minHeight + 'px' }"></div>
  </div>
</template>

<script>
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import http from '@/api/http'

// 注册字体大小白名单
const Size = Quill.import('attributors/style/size')
Size.whitelist = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']
Quill.register(Size, true)

export default {
  name: 'i-editor-pro',

  props: {
    value: {
      type: String,
      default: ''
    },
    minHeight: {
      type: Number,
      default: 400
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '请输入内容...'
    },
  },

  data() {
    return {
      quill: null,
      innerValue: '',
      isChanging: false,
      lastRange: null,
    }
  },

  methods: {
    initEditor() {
      const _self = this

      this.quill = new Quill(this.$refs.editorBox, {
        theme: 'snow',
        placeholder: this.placeholder,
        readOnly: this.readOnly,
        modules: {
          toolbar: this.$refs.toolbar,
          history: {
            delay: 1000,
            maxStack: 100,
            userOnly: true,
          },
        },
      })

      // 自定义图片上传
      const toolbar = this.quill.getModule('toolbar')
      toolbar.addHandler('image', () => {
        const input = document.createElement('input')
        input.setAttribute('type', 'file')
        input.setAttribute('accept', 'image/*')
        input.click()
        input.onchange = () => {
          const file = input.files[0]
          if (!file) return
          const formData = new FormData()
          formData.append('image', file)
          http.upload(formData).then(res => {
            if (res && res.status === 'success') {
              const range = _self.quill.getSelection(true)
              _self.quill.insertEmbed(range.index, 'image', res.data.file_url)
              _self.quill.setSelection(range.index + 1)
            }
          })
        }
      })

      // 保存光标位置——点击外部元素时 selection 会丢失，提前存储
      this.quill.on('selection-change', (range) => {
        if (range) {
          _self.lastRange = { index: range.index, length: range.length }
        }
      })

      // 内容变化回调
      this.quill.on('text-change', () => {
        if (_self.isChanging) return
        const html = _self.$refs.editorBox.querySelector('.ql-editor').innerHTML
        _self.innerValue = html
        _self.$emit('input', html)
      })

      // 回填初始值
      if (this.value) {
        this.isChanging = true
        this.quill.clipboard.dangerouslyPasteHTML(this.value)
        this.innerValue = this.value
        this.$nextTick(() => { this.isChanging = false })
      }
    },

    /**
     * 在光标处插入 HTML
     * 使用 lastRange 保证点击外部按钮后光标位置不丢失
     */
    insertHtml(html) {
      if (!this.quill) return
      const index = this.lastRange ? this.lastRange.index : (this.quill.getLength() - 1)
      this.quill.focus()
      this.quill.clipboard.dangerouslyPasteHTML(index, html)
      this.$nextTick(() => {
        this.quill.setSelection(index + 1, 0)
      })
    },

    /**
     * 获取当前 HTML 内容
     */
    getHtml() {
      if (!this.quill) return ''
      return this.$refs.editorBox.querySelector('.ql-editor').innerHTML
    },

    /**
     * 设置 HTML 内容（覆盖全部）
     */
    setHtml(html) {
      if (!this.quill) return
      this.isChanging = true
      this.quill.clipboard.dangerouslyPasteHTML(html)
      this.innerValue = html
      this.$nextTick(() => {
        this.isChanging = false
        this.$emit('input', html)
      })
    },

    /**
     * 获取纯文本
     */
    getText() {
      return this.quill ? this.quill.getText() : ''
    },

    /**
     * 清空
     */
    clear() {
      if (this.quill) {
        this.quill.setContents([])
        this.innerValue = ''
      }
    },
  },

  watch: {
    value(newVal) {
      if (!this.quill) return
      if (newVal === this.innerValue) return
      this.isChanging = true
      this.quill.clipboard.dangerouslyPasteHTML(newVal || '')
      this.innerValue = newVal || ''
      this.$nextTick(() => {
        this.isChanging = false
      })
    },
    readOnly(val) {
      if (this.quill) {
        this.quill.enable(!val)
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initEditor()
    })
  },

  beforeDestroy() {
    this.quill = null
  },
}
</script>

<style>
.i-editor-pro-wrap {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  line-height: 1.6;
}

.i-editor-pro-wrap .ql-toolbar.ql-snow {
  border: none;
  border-bottom: 1px solid #e4e7ed;
  background: #fafafa;
  padding: 6px 8px;
  flex-wrap: wrap;
}

.i-editor-pro-wrap .ql-container.ql-snow {
  border: none;
}

.i-editor-pro-wrap .editor-content .ql-editor {
  min-height: inherit;
  font-size: 14px;
  line-height: 1.8;
  padding: 16px 20px;
}

.i-editor-pro-wrap .editor-content .ql-editor p {
  margin-bottom: 6px;
}

.i-editor-pro-wrap .ql-editor table {
  border-collapse: collapse;
  width: 100%;
  margin: 10px 0;
}

.i-editor-pro-wrap .ql-editor table td,
.i-editor-pro-wrap .ql-editor table th {
  border: 1px solid #dcdfe6;
  padding: 6px 10px;
  min-width: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/i-editor-pro.vue